// Forecasting > Scaling Results: lists the client's scaling runs with their
// status (useScalingRuns) and renders a Completed run with the existing
// SaturationCurve pieces, the same view the Scaling (Preview) page shows for
// its local fixture. In Progress and Failed runs show their status instead.
import './ScalingResults.scss';
import { ReactElement, useEffect, useState } from 'react';
import { Button } from '@Components/Button/Button';
import { Appearance, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { Spinner } from '@Components/Spinner/Spinner';
import { useSetError } from '@src/redux/modals';
import { RunSelector } from '../components/RunSelector';
import { pooledPlatformLabel, pooledTacticLabel } from '../forecasting-utils';
import { ScalingAnalysis, ScalingAnalysisStatus } from '../scalingAnalysisContract';
import { ScalingResultState, useScalingResult, useScalingRuns } from '../useScalingRuns';
import {
  comboOptionItems,
  comboValueFor,
  scalingRunLabel,
  spendCeilingNote
} from './scaling-run-utils';
import { ScalingAnalysisTab } from './ScalingAnalysisTab';
import {
  findCombo,
  kpiOptions,
  OutcomeKey,
  platformOptions,
  ScalingRunFile,
  splitComboValue,
  tacticOptions
} from './scalingData';

const OUTCOME_LABEL: Record<OutcomeKey, string> = { volume: 'Volume', revenue: 'Revenue' };
const OUTCOME_KEY: Record<string, OutcomeKey> = { Volume: 'volume', Revenue: 'revenue' };

const scalingRunKey = (run: ScalingAnalysis) => run.id;

// One Completed run's selectors + curve. Keyed by run in the parent, so
// switching runs re-seeds the selection from the new run's first combo.
const ScalingRunView = ({ data }: { data: ScalingRunFile }): ReactElement => {
  const [platform, setPlatform] = useState(() => platformOptions(data)[0]);
  const [tactic, setTactic] = useState(() => tacticOptions(data, platformOptions(data)[0])[0]);
  const [kpi, setKpi] = useState(() => kpiOptions(data, platform, tactic)[0]);
  const [outcome, setOutcome] = useState<OutcomeKey>('volume');

  const combo = findCombo(data, platform, tactic, kpi);
  const outcomes = (combo ? Object.keys(combo.outcomes) : []) as OutcomeKey[];
  const resolvedOutcome = outcomes.includes(outcome) ? outcome : outcomes[0];
  const outcomeData = combo && resolvedOutcome ? combo.outcomes[resolvedOutcome] : undefined;
  const ceilingNote = spendCeilingNote(data);

  return (
    <>
      {ceilingNote && <p className='scaling-results-note'>{ceilingNote}</p>}
      <div className='scaling-results-selectors'>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          stretch={StretchMode.FIT}
          label='Channel'
          value={comboValueFor(data, platform, tactic)}
          options={comboOptionItems(data)}
          onChange={(value: string) => {
            const { platform: p, tactic: t } = splitComboValue(value);
            setPlatform(p);
            setTactic(t);
            setKpi(kpiOptions(data, p, t)[0]);
          }}
        />
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          stretch={StretchMode.FIT}
          label='KPI'
          value={kpi}
          options={kpiOptions(data, platform, tactic)}
          onChange={setKpi}
        />
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          stretch={StretchMode.FIT}
          label='Outcome'
          value={resolvedOutcome ? OUTCOME_LABEL[resolvedOutcome] : ''}
          options={outcomes.map(key => OUTCOME_LABEL[key])}
          onChange={(label: string) => setOutcome(OUTCOME_KEY[label])}
        />
      </div>
      {outcomeData ? (
        <ScalingAnalysisTab
          channel={data.channel}
          platform={pooledPlatformLabel(platform)}
          tactic={pooledTacticLabel(tactic)}
          kpi={kpi}
          outcomeData={outcomeData}
        />
      ) : (
        <div className='scaling-results-empty'>No scaling data for this selection.</div>
      )}
    </>
  );
};

// What the selected run shows, by its status.
const ScalingRunBody = ({
  runId,
  result,
  loadFailed,
  onRetry
}: {
  runId: string;
  result: ScalingResultState | undefined;
  /** The results request itself failed (after React Query's retry). */
  loadFailed: boolean;
  onRetry: () => void;
}): ReactElement => {
  if (!result && loadFailed) {
    return (
      <div className='scaling-results-empty' data-testid='scaling-run-load-failed'>
        <p>Couldn&apos;t load this run&apos;s results.</p>
        <Button appearance={Appearance.OUTLINED} onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  if (!result) {
    return <Spinner />;
  }
  switch (result.status) {
    case ScalingAnalysisStatus.IN_PROGRESS:
      return (
        <div className='scaling-results-empty' data-testid='scaling-run-in-progress'>
          This scaling analysis is still running. You&apos;ll get an email when it finishes; this
          page checks again every 30 seconds.
        </div>
      );
    case ScalingAnalysisStatus.FAILED:
      return (
        <div className='scaling-results-empty' data-testid='scaling-run-failed'>
          This scaling analysis failed: {result.error}
        </div>
      );
    case 'error':
      return (
        <div className='scaling-results-empty' data-testid='scaling-run-error'>
          Couldn&apos;t load this run: {result.message}
        </div>
      );
    default:
      return <ScalingRunView key={runId} data={result.run} />;
  }
};

interface ScalingResultsProps {
  cid: string;
  onGoToRequestForecast: () => void;
}

export const ScalingResults = ({ cid, onGoToRequestForecast }: ScalingResultsProps) => {
  const setError = useSetError();
  const company = cid || undefined;
  const runsQuery = useScalingRuns(company);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const runs = runsQuery.data ?? [];
  // Newest by default; a refetch that drops the picked run falls back to newest too.
  const run = runs.find(r => r.id === selectedId) ?? runs[0];
  const resultQuery = useScalingResult(company, run?.id);

  // Only a load with nothing to show raises the shared error modal: a failed
  // 30s background poll keeps the last good data quietly. (A run that failed
  // is data, shown inline.)
  const listLoadError = runsQuery.data ? null : runsQuery.error;
  useEffect(() => {
    if (listLoadError) {
      setError({
        title: "Couldn't load scaling results",
        message: `Failed to load scaling results: ${listLoadError.message}`,
        reportError: listLoadError
      });
    }
  }, [listLoadError, setError]);

  if (!company || runsQuery.isLoading) {
    return <Spinner />;
  }
  if (listLoadError) {
    return (
      <div className='scaling-results-empty' data-testid='scaling-results-error'>
        <p>Couldn&apos;t load scaling results.</p>
        <Button appearance={Appearance.OUTLINED} onClick={() => runsQuery.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  if (runs.length === 0) {
    return (
      <div className='scaling-results-empty' data-testid='scaling-results-empty'>
        <p>No scaling analyses yet. Request one from the Request Forecast tab.</p>
        <Button appearance={Appearance.OUTLINED} onClick={onGoToRequestForecast}>
          Request Forecast
        </Button>
      </div>
    );
  }

  // Spins only for a user's refresh, not the 30s background polls.
  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([
      runsQuery.refetch(),
      resultQuery.data?.status === ScalingAnalysisStatus.COMPLETED ? null : resultQuery.refetch()
    ]);
    setRefreshing(false);
  };

  return (
    <div className='scaling-results' data-testid='scaling-results'>
      <div className='scaling-results-toolbar' data-testid='scaling-results-toolbar'>
        <span className='scaling-results-label'>Scaling run</span>
        <RunSelector
          runs={runs}
          runKey={scalingRunKey}
          formatLabel={scalingRunLabel}
          selectedKey={run.id}
          onSelectRun={setSelectedId}
          onRefresh={refresh}
          isRefreshing={refreshing}
        />
      </div>
      <ScalingRunBody
        runId={run.id}
        result={resultQuery.data}
        loadFailed={resultQuery.isError}
        onRetry={() => resultQuery.refetch()}
      />
    </div>
  );
};
